// Monta um PDF a partir de páginas JPEG, sem biblioteca: cada página é uma imagem ocupando a página inteira.
// É o formato de um escaneado comum, e é o que a otimização de processos produz (otimizarArquivo.js).
// Função pura: recebe os bytes de cada JPEG e devolve os bytes do PDF.

// Todo o texto estrutural do PDF é de 1 byte por caractere (ASCII, e o comentário binário do cabeçalho)
const texto = (s) => {
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i) & 0xff;
  return bytes;
};

// paginas: [{ jpeg: Uint8Array, larguraPx, alturaPx, larguraPt, alturaPt }]
// (pt = pontos do PDF, 1/72 polegada: é o tamanho da folha; px = resolução da imagem)
export function montarPdfDeJpegs(paginas) {
  if (!paginas || paginas.length === 0) throw new Error('Nenhuma página para montar o PDF');

  // Objetos: 1 catálogo, 2 árvore de páginas, e para cada página: página, conteúdo, imagem
  const partes = [];
  const offsets = [];
  let tamanho = 0;
  const escrever = (bytes) => { partes.push(bytes); tamanho += bytes.length; };
  const abrirObjeto = (numero) => { offsets[numero] = tamanho; escrever(texto(`${numero} 0 obj\n`)); };

  escrever(texto('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n'));

  const idsPaginas = paginas.map((_, i) => 3 + i * 3);
  abrirObjeto(1);
  escrever(texto('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n'));
  abrirObjeto(2);
  escrever(texto(`<< /Type /Pages /Kids [${idsPaginas.map((id) => `${id} 0 R`).join(' ')}] /Count ${paginas.length} >>\nendobj\n`));

  paginas.forEach((pagina, i) => {
    const idPagina = 3 + i * 3;
    const idConteudo = idPagina + 1;
    const idImagem = idPagina + 2;
    const w = numero(pagina.larguraPt);
    const h = numero(pagina.alturaPt);

    abrirObjeto(idPagina);
    escrever(texto(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] `
      + `/Resources << /XObject << /Im0 ${idImagem} 0 R >> >> /Contents ${idConteudo} 0 R >>\nendobj\n`));

    const desenho = texto(`q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q`);
    abrirObjeto(idConteudo);
    escrever(texto(`<< /Length ${desenho.length} >>\nstream\n`));
    escrever(desenho);
    escrever(texto('\nendstream\nendobj\n'));

    abrirObjeto(idImagem);
    escrever(texto(`<< /Type /XObject /Subtype /Image /Width ${pagina.larguraPx} /Height ${pagina.alturaPx} `
      + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${pagina.jpeg.length} >>\nstream\n`));
    escrever(pagina.jpeg);
    escrever(texto('\nendstream\nendobj\n'));
  });

  const totalObjetos = 3 + paginas.length * 3;
  const inicioXref = tamanho;
  let xref = `xref\n0 ${totalObjetos}\n0000000000 65535 f \n`;
  for (let n = 1; n < totalObjetos; n++) {
    xref += `${String(offsets[n]).padStart(10, '0')} 00000 n \n`;
  }
  escrever(texto(`${xref}trailer\n<< /Size ${totalObjetos} /Root 1 0 R >>\nstartxref\n${inicioXref}\n%%EOF\n`));

  const pdf = new Uint8Array(tamanho);
  let posicao = 0;
  for (const parte of partes) {
    pdf.set(parte, posicao);
    posicao += parte.length;
  }
  return pdf;
}

// Quanto o PDF acrescenta além das imagens (cabeçalhos, xref): pequeno, mas entra na conta do limite
export const SOBRA_POR_PAGINA = 400;
export const SOBRA_FIXA = 300;

// Divide as páginas em partes que caibam no limite, mantendo a ordem. tamanhos = bytes do JPEG de cada página.
// Devolve os índices de cada parte: [[0,1,2],[3,4]]. Uma página sozinha maior que o limite não tem como caber:
// devolve null (quem chama avisa que aquela página precisa ser escaneada com menos resolução).
export function dividirEmPartes(tamanhos, limiteBytes) {
  const partes = [];
  let atual = [];
  let somaAtual = SOBRA_FIXA;
  for (let i = 0; i < tamanhos.length; i++) {
    const custo = tamanhos[i] + SOBRA_POR_PAGINA;
    if (SOBRA_FIXA + custo > limiteBytes) return null;
    if (atual.length > 0 && somaAtual + custo > limiteBytes) {
      partes.push(atual);
      atual = [];
      somaAtual = SOBRA_FIXA;
    }
    atual.push(i);
    somaAtual += custo;
  }
  if (atual.length) partes.push(atual);
  return partes;
}

export function tamanhoEstimado(tamanhos) {
  return SOBRA_FIXA + tamanhos.reduce((soma, t) => soma + t + SOBRA_POR_PAGINA, 0);
}

function numero(valor) {
  return Math.round(valor * 100) / 100;
}
